import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  computed,
  effect,
  inject,
  input,
  output,
  signal,
  untracked,
} from '@angular/core';
import { DatePipe, DecimalPipe } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatDialog } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { timer } from 'rxjs';

import { AiCallProduct } from '../../core/admin/ai-call.service';
import { AiComparisonService } from '../../core/admin/ai-comparison.service';
import { AiComparisonRow, AiComparisons } from '../../core/admin/ai-comparison.types';
import { AiMarkComponent } from '../ai-mark/ai-mark';
import { IconComponent } from '../icon/icon';
import { ZarPipe } from '../pipes/zar.pipe';
import { AiRunDialogComponent, AiRunDialogData } from './ai-run-dialog';

const POLL_MS = 15_000;

const DOC_CHECK = 'DOC_CHECK';

/**
 * Re-run one of this file's AI calls on another model and read the two side by
 * side (Ops-only, N4). A comparison never changes a live result. Only a system
 * admin can start one, because it spends money; anyone on the file can read them.
 *
 * Self-fetching, like the last-call panel: it has its own failures and its own
 * polling, and neither belongs to the page it sits on.
 *
 * `purpose` narrows it to one kind of call. `embedded` is the run list alone, for a
 * host that names it and starts its own runs; `reloadOn` tells it when one has.
 */
@Component({
  selector: 'app-ai-comparison-panel',
  imports: [
    DatePipe,
    DecimalPipe,
    MatButtonModule,
    MatCardModule,
    MatFormFieldModule,
    MatSelectModule,
    AiMarkComponent,
    IconComponent,
    ZarPipe,
  ],
  templateUrl: './ai-comparison-panel.html',
  styleUrl: './ai-comparison-panel.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AiComparisonPanelComponent {
  private readonly service = inject(AiComparisonService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly dialog = inject(MatDialog);

  readonly product = input.required<AiCallProduct>();
  readonly targetId = input.required<string>();
  readonly purpose = input('');
  readonly embedded = input(false);
  readonly reloadOn = input<unknown>(null);
  /** A run is under way that may not be listed yet, so keep looking. */
  readonly polling = input(false);
  readonly runCount = output<number>();

  protected readonly data = signal<AiComparisons | null>(null);
  protected readonly loadError = signal(false);
  protected readonly selectedBaseline = signal('');
  protected readonly selectedModel = signal('');
  protected readonly starting = signal(false);
  protected readonly refusal = signal('');

  protected readonly baselines = computed<AiComparisonRow[]>(() => {
    const purpose = this.purpose();
    const baselines = this.data()?.baselines ?? [];
    return purpose ? baselines.filter((b) => b.purpose === purpose) : baselines;
  });

  protected readonly baseline = computed<AiComparisonRow | null>(() => {
    const baselines = this.baselines();
    return baselines.find((b) => b.short_id === this.selectedBaseline()) ?? baselines[0] ?? null;
  });

  /** The chosen call's whole history, live runs and re-runs together, newest first. */
  protected readonly rows = computed<AiComparisonRow[]>(() => {
    const purpose = this.baseline()?.purpose ?? this.purpose();
    const target = this.baseline()?.target_id;
    const data = this.data();
    if (!data || !purpose) return [];
    const onThisCall = (row: AiComparisonRow) => !target || row.target_id === target;
    return [
      ...data.live_runs.filter((r) => r.purpose === purpose && onThisCall(r)),
      ...data.comparisons.filter((c) => c.compared_purpose === purpose && onThisCall(c)),
    ].sort((a, b) => b.created_at.localeCompare(a.created_at));
  });

  protected readonly totalCost = computed(() =>
    this.rows().reduce((sum, row) => sum + Number(row.cost_zar), 0),
  );

  protected readonly running = computed(() =>
    (this.data()?.comparisons ?? []).some((c) => c.status === 'RUNNING' && !c.abandoned),
  );

  private readonly liveRunning = computed(() =>
    (this.data()?.live_runs ?? []).some((r) => r.status === 'RUNNING' && !r.abandoned),
  );

  protected readonly canStart = computed(
    () =>
      !!this.data()?.can_run &&
      !!this.baseline() &&
      !!this.selectedModel() &&
      !this.starting(),
  );

  /** A second difference beside the model, so the reader is told before pressing. */
  protected readonly losesThinking = computed(() => {
    const model = this.data()?.models.find((m) => m.model === this.selectedModel());
    return !!model && !model.thinking && this.baseline()?.purpose !== DOC_CHECK;
  });

  constructor() {
    effect(() => {
      this.reloadOn();
      untracked(() => this.load());
    });
    timer(POLL_MS, POLL_MS)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        if (this.polling() || this.running() || this.liveRunning()) this.load();
      });
  }

  protected isLive(row: AiComparisonRow): boolean {
    return !row.compared_purpose;
  }

  /** The live run whose result the file stands on; any other is history. */
  protected isCurrent(row: AiComparisonRow): boolean {
    return row.short_id === this.baseline()?.short_id;
  }

  protected baselineLabel(row: AiComparisonRow): string {
    return row.purpose === DOC_CHECK
      ? `${row.purpose_display} · ${row.target_id}`
      : row.purpose_display;
  }

  protected answerLabel(row: AiComparisonRow): string {
    if (row.abandoned) return 'Stopped';
    if (row.status === 'RUNNING') return 'Running';
    if (row.status === 'FAILED') return 'Failed';
    return `Complete · ${row.response_keys.length} fields`;
  }

  protected duration(ms: number): string {
    if (ms < 60_000) return `${Math.round(ms / 1000)} sec`;
    return `${Math.round(ms / 60_000)} min`;
  }

  protected sameInputLabel(row: AiComparisonRow): string {
    if (this.isLive(row) || row.same_input === null) return '—';
    return row.same_input ? 'Same' : 'Different';
  }

  protected start(): void {
    const live = this.baseline();
    if (!this.canStart() || !live) return;
    this.starting.set(true);
    this.refusal.set('');
    this.service
      .start(this.product(), this.targetId(), live.short_id, this.selectedModel())
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.starting.set(false);
          this.load();
        },
        error: (err: HttpErrorResponse) => {
          this.starting.set(false);
          this.refusal.set(err.error?.detail ?? "The comparison didn't start. Try again.");
        },
      });
  }

  protected view(row: AiComparisonRow): void {
    this.dialog.open(AiRunDialogComponent, {
      data: {
        product: this.product(),
        targetId: this.targetId(),
        row,
      } satisfies AiRunDialogData,
      width: '64rem',
      maxWidth: '95vw',
    });
  }

  private load(): void {
    this.service
      .forFile(this.product(), this.targetId())
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (data) => {
          this.data.set(data);
          this.runCount.emit(this.rows().length);
          this.loadError.set(false);
        },
        error: () => this.loadError.set(true),
      });
  }
}
